import type React from "react";
import {useState} from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, TextField} from "@mui/material";
import type {FinishTaxonomyApprovalVariables} from "~/pages/taxonomy-builder-v2/approval/hooks/useFinishTaxonomyApprovalMutation";

type FinishReviewDialogProps = {
    decision: FinishTaxonomyApprovalVariables['decision'];
    /** The reply saved on the request so far */
    initialReply: string;
    isSaving: boolean;
    onCancel: () => void;
    onConfirm: (reply: string) => void;
};

/** Confirms approving or rejecting the request, with a reply to the requester. Mount it fresh per opening. */
export const FinishReviewDialog: React.FC<FinishReviewDialogProps> = ({
                                                                          decision,
                                                                          initialReply,
                                                                          isSaving,
                                                                          onCancel,
                                                                          onConfirm,
                                                                      }) => {
    const [reply, setReply] = useState(initialReply);
    const approve = decision === 'approve';

    return (
        <Dialog open onClose={isSaving ? undefined : onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>{approve ? 'Approve the changes' : 'Reject the changes'}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 0.5}}>
                    <DialogContentText>
                        {approve
                            ? 'The taxonomy as you see it here, without the changes you reverted, becomes the new taxonomy. Categorized data is not changed.'
                            : 'None of the changes are applied. The requester can edit the taxonomy again and send it for approval anew.'}
                    </DialogContentText>
                    <TextField
                        label="Reply to the requester"
                        placeholder="Optional"
                        value={reply}
                        onChange={e => setReply(e.target.value)}
                        multiline
                        minRows={2}
                        fullWidth
                        size="small"
                        autoFocus
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={isSaving}>Cancel</Button>
                <Button
                    variant="contained"
                    color={approve ? 'primary' : 'error'}
                    onClick={() => onConfirm(reply.trim())}
                    loading={isSaving}
                >
                    {approve ? 'Approve' : 'Reject'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
